<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { AdminTicket } from '@/stores/useTicketAdminStore'
import { useTicketAdminStore } from '@/stores/useTicketAdminStore'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

const props = defineProps<{ ticket: AdminTicket }>()

const router = useRouter()
const adminStore = useTicketAdminStore()
const usersStore = useBoostCoreUsersStore()
const permStore = useBoostCorePermissionStore()

const relatedRequest = ref<{ _id: string; code: string; status: string } | null>(null)
const showTransform = ref(false)
const transformCommentBack = ref(false)
const transformCloseTicket = ref(false)

const newComment = ref('')
const newCommentPublic = ref(false)
const newCommentFiles = ref<File[]>([])
const fileInput = ref<HTMLInputElement | null>(null)
const newTag = ref('')

const STATUSES = [
  { value: 'NEW', label: 'NOUVEAU', color: 'bg-blue-100 text-blue-800' },
  { value: 'OPEN', label: 'OUVERT', color: 'bg-green-100 text-green-800' },
  { value: 'WAITING', label: 'EN ATTENTE', color: 'bg-orange-100 text-orange-800' },
  { value: 'PAUSED', label: 'EN PAUSE', color: 'bg-gray-100 text-gray-800' },
  { value: 'RESOLVED', label: 'RÉSOLU', color: 'bg-teal-100 text-teal-800' },
  { value: 'CLOSED', label: 'FERMÉ', color: 'bg-red-100 text-red-800' },
]

const requesterDisplay = computed(() => {
  const u = props.ticket.ticketRequester?.user
  if (u) return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email
  const c = props.ticket.ticketRequester?.contact
  if (c) return `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim() || c.email
  return '—'
})

const assignedDisplay = computed(() => {
  const u = props.ticket.assignedUser
  if (!u) return null
  return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email
})

const sortedComments = computed(() => {
  const list = props.ticket.comments ?? []
  return [...list].sort((a, b) => a.date - b.date)
})

async function changeStatus(event: Event) {
  const value = (event.target as HTMLSelectElement).value
  if (!value || value === props.ticket.ticketStatus) return
  await adminStore.updateStatus(props.ticket.uuid, value)
}

async function changeAssignee(event: Event) {
  const value = (event.target as HTMLSelectElement).value
  if (value === '__current__') {
    await adminStore.assign(props.ticket.uuid)
    return
  }
  if (value === '__unassign__') {
    await adminStore.assign(props.ticket.uuid, null)
    return
  }
  await adminStore.assign(props.ticket.uuid, value)
}

async function assignToMe() {
  await adminStore.assign(props.ticket.uuid)
}

async function removeTag(tag: string) {
  const current = props.ticket.tags ?? []
  const next = current.filter((t) => t !== tag)
  if (next.length === current.length) return
  await adminStore.updateTags(props.ticket.uuid, next)
}

async function addTag() {
  const value = newTag.value.trim()
  if (!value) return
  const current = props.ticket.tags ?? []
  if (current.some((t) => t.toUpperCase() === value.toUpperCase())) {
    newTag.value = ''
    return
  }
  await adminStore.updateTags(props.ticket.uuid, [...current, value])
  newTag.value = ''
}

function onFilesSelected(e: Event) {
  const target = e.target as HTMLInputElement
  newCommentFiles.value = target.files ? Array.from(target.files) : []
}

async function submitComment() {
  const text = newComment.value.trim()
  if (!text) return
  await adminStore.addComment(props.ticket.uuid, text, newCommentPublic.value, newCommentFiles.value)
  newComment.value = ''
  newCommentFiles.value = []
  if (fileInput.value) fileInput.value.value = ''
}

const isCurrentUserAssigned = computed(
  () => !!permStore.user && props.ticket.assignedUserUuid === permStore.user.uuid,
)

async function submitTransform() {
  const res = await adminStore.transformToRequest(props.ticket.uuid, {
    commentBack: transformCommentBack.value,
    closeTicket: transformCloseTicket.value,
  })
  showTransform.value = false
  if (res?.request?._id) {
    router.push(`/admin/pole-dev/${res.request._id}`)
  }
}

watch(
  () => props.ticket.uuid,
  async (uuid) => {
    if (uuid) {
      relatedRequest.value = await adminStore.fetchRelatedRequest(uuid)
    }
  },
  { immediate: true },
)

onMounted(async () => {
  if (usersStore.users.length === 0) await usersStore.fetchUsers()
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div class="card bg-base-100 shadow p-4">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div class="text-xs text-base-content/60">
            #{{ ticket.internalReference }} · {{ ticket.source?.name ?? '—' }} · réf ext. {{ ticket.externalReference }}
          </div>
          <h2 class="text-xl font-semibold mt-1">{{ ticket.title }}</h2>
          <div class="text-sm text-base-content/70 mt-2">
            Demandeur : <strong>{{ requesterDisplay }}</strong>
          </div>
          <div class="flex gap-1 mt-2 flex-wrap items-center">
            <span
              v-for="t in ticket.tags ?? []"
              :key="t"
              class="badge badge-outline badge-sm gap-1"
            >
              {{ t }}
              <button
                type="button"
                class="text-base-content/60 hover:text-error"
                title="Retirer ce tag"
                :disabled="adminStore.loading"
                @click="removeTag(t)"
              >
                ×
              </button>
            </span>
            <div class="join">
              <input
                v-model="newTag"
                type="text"
                placeholder="Nouveau tag"
                class="input input-bordered input-xs join-item w-32"
                @keydown.enter.prevent="addTag"
              />
              <button class="btn btn-xs join-item" :disabled="adminStore.loading || !newTag.trim()" @click="addTag">
                +
              </button>
            </div>
          </div>
        </div>
        <div class="text-xs text-right text-base-content/60">
          <div>Créé : {{ new Date(ticket.dateCreated).toLocaleString('fr-FR') }}</div>
          <div>Modifié : {{ new Date(ticket.dateUpdated).toLocaleString('fr-FR') }}</div>
          <div v-if="ticket.lastSyncedAt">Sync : {{ new Date(ticket.lastSyncedAt).toLocaleString('fr-FR') }}</div>
        </div>
      </div>
    </div>

    <!-- Bandeau demande pôle dev -->
    <div v-if="relatedRequest" class="alert alert-info">
      <span class="text-sm">
        Demande pôle dev associée :
        <router-link :to="`/admin/pole-dev/${relatedRequest._id}`" class="font-semibold link link-primary">
          {{ relatedRequest.code }}
        </router-link>
        <span class="badge badge-outline badge-sm ml-2">{{ relatedRequest.status }}</span>
      </span>
    </div>

    <!-- Actions admin -->
    <div class="card bg-base-100 shadow p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-semibold">Actions</h3>
        <button
          v-if="!relatedRequest"
          class="btn btn-sm btn-outline btn-primary"
          :disabled="adminStore.loading"
          @click="showTransform = true"
        >
          Transformer en demande
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label class="form-control">
          <span class="label-text text-xs mb-1">Statut</span>
          <select
            class="select select-bordered select-sm"
            :value="ticket.ticketStatus"
            @change="changeStatus"
            :disabled="adminStore.loading"
          >
            <option v-for="s in STATUSES" :key="s.value" :value="s.value">{{ s.label }}</option>
          </select>
        </label>

        <label class="form-control md:col-span-2">
          <span class="label-text text-xs mb-1">
            Assigné :
            <strong v-if="assignedDisplay">{{ assignedDisplay }}</strong>
            <span v-else class="text-base-content/60">— non assigné</span>
            <button
              v-if="!isCurrentUserAssigned"
              class="link link-primary ml-2 text-xs"
              @click="assignToMe"
              :disabled="adminStore.loading"
            >
              S'attribuer
            </button>
          </span>
          <select
            class="select select-bordered select-sm"
            :value="ticket.assignedUserUuid ?? ''"
            @change="changeAssignee"
            :disabled="adminStore.loading"
          >
            <option value="__unassign__">— Désassigner</option>
            <option value="__current__">Moi</option>
            <option disabled>──────────</option>
            <option v-for="u in usersStore.users" :key="u.uuid" :value="u.uuid">
              {{ usersStore.getUserLabel(u) }}
            </option>
          </select>
        </label>
      </div>
    </div>

    <!-- Description -->
    <div class="card bg-base-100 shadow p-4">
      <h3 class="font-semibold mb-2">Description</h3>
      <div class="text-sm whitespace-pre-wrap break-words text-base-content/80">{{ ticket.description }}</div>
    </div>

    <!-- Commentaires -->
    <div class="card bg-base-100 shadow p-4">
      <h3 class="font-semibold mb-3">Commentaires</h3>
      <div v-if="sortedComments.length === 0" class="text-sm text-base-content/60">Aucun commentaire.</div>
      <div v-else class="flex flex-col gap-3">
        <div
          v-for="(c, i) in sortedComments"
          :key="i"
          class="rounded border p-3"
          :class="c.publicComment ? 'border-base-300 bg-base-100' : 'border-warning/30 bg-warning/5'"
        >
          <div class="flex items-center justify-between mb-1">
            <div class="text-xs font-semibold">
              {{ c.author }}<span v-if="c.authorDetail" class="text-base-content/60"> ({{ c.authorDetail }})</span>
            </div>
            <div class="flex items-center gap-2">
              <span v-if="c.publicComment" class="badge badge-success badge-xs">PUBLIC</span>
              <span v-else class="badge badge-warning badge-xs">PRIVÉ</span>
              <span class="text-xs text-base-content/60">{{ new Date(c.date).toLocaleString('fr-FR') }}</span>
            </div>
          </div>
          <div class="text-sm whitespace-pre-wrap break-words">{{ c.comment }}</div>
        </div>
      </div>

      <!-- Form ajout commentaire -->
      <div class="mt-4 pt-4 border-t">
        <textarea
          v-model="newComment"
          class="textarea textarea-bordered w-full"
          rows="3"
          placeholder="Ajouter un commentaire…"
          :disabled="adminStore.loading"
        />
        <div class="flex items-center justify-between mt-2 gap-3 flex-wrap">
          <label class="cursor-pointer label gap-2">
            <input
              ref="fileInput"
              type="file"
              multiple
              class="file-input file-input-bordered file-input-xs"
              @change="onFilesSelected"
            />
          </label>
          <div class="flex items-center gap-3">
            <label class="cursor-pointer label gap-2">
              <input v-model="newCommentPublic" type="checkbox" class="checkbox checkbox-sm" />
              <span class="label-text text-xs">Public (visible client)</span>
            </label>
            <button
              class="btn btn-primary btn-sm"
              @click="submitComment"
              :disabled="adminStore.loading || !newComment.trim()"
            >
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Transformer en demande -->
    <div v-if="showTransform" class="modal modal-open">
      <div class="modal-box max-w-md">
        <h3 class="font-semibold mb-3">Transformer en demande pôle dev</h3>
        <p class="text-sm text-base-content/70 mb-3">
          Une demande sera créée à partir du titre/description du ticket. Action idempotente :
          si une demande existe déjà pour ce ticket, on l'ouvre.
        </p>
        <label class="flex items-center gap-2 cursor-pointer mb-2">
          <input v-model="transformCommentBack" type="checkbox" class="checkbox checkbox-sm" />
          <span class="text-sm">Poster un commentaire public au demandeur pour l'informer</span>
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="transformCloseTicket" type="checkbox" class="checkbox checkbox-sm" />
          <span class="text-sm">Marquer le ticket comme RÉSOLU</span>
        </label>
        <div class="modal-action">
          <button class="btn btn-sm" @click="showTransform = false">Annuler</button>
          <button class="btn btn-sm btn-primary" :disabled="adminStore.loading" @click="submitTransform">Confirmer</button>
        </div>
      </div>
    </div>
  </div>
</template>
